import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Crown, Landmark, Receipt, ShieldCheck, TrendingUp, TrendingDown, Wallet,
  AlertTriangle, Users2, Truck, CalendarClock, FileText, Info,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { PortalHeader, PortalBottomNav, NavItem } from '../shared/PortalChrome';
import { useAutoRefresh } from '../shared/useAutoRefresh';
import { useCountUp } from '../shared/useCountUp';
import {
  CeoSummary, CeoExpiry, CeoBank, ceoSummary, ceoSummaryViaSso,
  ceoRequestCode, ceoVerifyCode, ceoApiConfigured, assetDeskToken,
} from './ceoApi';
import { CEO_MOCK } from './mockCeo';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// CEOPortal — the company-side executive console.
//
// This is the mobile rendering of the CRM SaaS company portal's CEO View
// (crm-saas /portal/ceo): profit & cashflow, money owed to the company,
// VAT position, top customers / suppliers and licence + visa compliance.
//
// It reads ONE aggregated endpoint (see ceoApi.ts) so the figures always
// match the web portal.
//
// No password. If the CEO is already signed into Asset Desk we exchange that
// session for a portal token and go straight in; the sign-in screen is only for
// company owners who aren't Asset Desk investors, and it uses an email code.
// With the CRM project unconfigured it renders preview data instead, exactly
// like the other portals fall back to mock.
// ============================================================

const ACCENT = '#B8933F'; // the company portal's gold — distinct from app gold
const isProdBuild = process.env.NODE_ENV === 'production';

interface Props {
  onChangeRole?: () => void;
  /**
   * Code-gated showcase (portals/demo/DemoShell): open straight onto CEO_MOCK,
   * the placeholder-company figures the "preview" control already uses, with
   * no sign-in and no CRM call. The shell also blocks API traffic outright.
   */
  demoMode?: boolean;
}

// `label` holds a translation key; resolved with t() where the nav is rendered.
const NAV: NavItem[] = [
  { id: 'overview', label: 'ceoExecutive', icon: Crown },
  { id: 'receivables', label: 'ceoOwed', icon: Landmark },
  { id: 'tax', label: 'ceoVat', icon: Receipt },
  { id: 'compliance', label: 'ceoCompliance', icon: ShieldCheck },
];

const CEOPortal: React.FC<Props> = ({ onChangeRole, demoMode }) => {
  const { t } = useLanguage();
  // How we got in — decides how a refresh re-authenticates. 'sso' can re-mint a
  // token silently from the live Asset Desk session; 'code' cannot, so its token
  // is reused until it expires.
  const [auth, setAuth] = useState<{ via: 'sso' } | { via: 'code'; token: string } | null>(null);
  // Showcase: start on the placeholder-company figures, which is exactly what
  // the existing "preview" control below loads. No sign-in, no real company.
  const [data, setData] = useState<CeoSummary | null>(demoMode ? CEO_MOCK : null);
  const [tab, setTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  // Silent SSO attempt on mount: undefined = still trying, false = fell through
  // to the sign-in screen. Skipped entirely when there's no session to exchange.
  // Showcase never attempts SSO: a staff member who happens to be signed in to
  // Asset Desk would otherwise trigger a real CRM exchange on mount (blocked by
  // the shell, but it would still surface an error over the sample figures).
  const [ssoTried, setSsoTried] = useState(demoMode || !(ceoApiConfigured() && assetDeskToken()));
  const [ssoError, setSsoError] = useState('');
  const [ssoErrorCode, setSsoErrorCode] = useState('');
  // Set when the user chooses to try a different email after being refused.
  const [forceCodeLogin, setForceCodeLogin] = useState(false);

  useEffect(() => {
    if (ssoTried) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await ceoSummaryViaSso();
        if (cancelled) return;
        setAuth({ via: 'sso' });
        setData(summary);
      } catch (ex: any) {
        // Signed into Asset Desk but not a company owner is worth saying out
        // loud — otherwise the sign-in screen looks like the code just failed.
        if (!cancelled) { setSsoError(ex?.message || ''); setSsoErrorCode(ex?.code || ''); }
      } finally {
        if (!cancelled) setSsoTried(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    if (!auth) return;
    setRefreshing(true);
    try {
      setData(auth.via === 'sso' ? await ceoSummaryViaSso() : await ceoSummary(auth.token));
    } catch {
      /* keep the figures already on screen */
    } finally {
      setRefreshing(false);
    }
  }, [auth]);

  // The company portal is read-mostly; a 60s poll keeps the numbers current
  // without the CEO having to pull to refresh.
  useAutoRefresh(refresh, { intervalMs: 60000, enabled: !!auth && data?.source === 'live' });

  if (!data) {
    if (!ssoTried) return <CEOLoading />;
    // Refused because this account isn't a company owner: requesting a code
    // would be a dead end, since the code is gated by the same check. Say so
    // plainly instead of showing a form that can never succeed.
    const blocked = ssoErrorCode === 'not_owner' || ssoErrorCode === 'no_company';
    if (blocked && !forceCodeLogin) {
      return (
        <CEOBlocked
          message={ssoError}
          onUseAnotherEmail={() => setForceCodeLogin(true)}
          onChangeRole={onChangeRole}
        />
      );
    }
    return (
      <CEOLogin
        notice={forceCodeLogin ? '' : ssoError}
        onSignedIn={(token, summary) => { setAuth({ via: 'code', token }); setData(summary); }}
        onPreview={() => setData(CEO_MOCK)}
        onChangeRole={onChangeRole}
      />
    );
  }

  const titles: Record<string, string> = {
    overview: t('ceoExecutiveSummary'),
    receivables: t('ceoMoneyOwed'),
    tax: t('ceoVatPosition'),
    compliance: t('ceoCompliance'),
  };

  return (
    <div className="pt-screen">
      <PortalHeader
        title={titles[tab]}
        subtitle={data.source === 'mock' ? t('ceoPreviewData') : data.company.name}
        accent={ACCENT}
        onRefresh={auth ? refresh : undefined}
        refreshing={refreshing}
        onChangeRole={onChangeRole}
      />
      <div className="pt-fade">
        {tab === 'overview' && <Executive d={data} onGoto={setTab} />}
        {tab === 'receivables' && <Receivables d={data} />}
        {tab === 'tax' && <Vat d={data} />}
        {tab === 'compliance' && <Compliance d={data} />}
      </div>
      <PortalBottomNav items={NAV.map((n) => ({ ...n, label: t(n.label) }))} active={tab} onChange={setTab} accent={ACCENT} />
    </div>
  );
};

// ---------------- Signing in via the existing Asset Desk session ----------------
const CEOLoading: React.FC = () => {
  const { t } = useLanguage();
  return (
  <div className="pt-screen pl-screen">
    <div className="pl-frame" aria-hidden="true" />
    <div className="pl-content" style={{ textAlign: 'center' }}>
      <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />
      <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
      <div className="pl-eyebrow">{t('ceoAccessEyebrow')}</div>
      <h1 className="pl-title">{t('ceoOpening')}</h1>
      <p className="pl-sub">{t('ceoOneMoment')}</p>
      <div style={{ marginTop: 26 }}><span className="pt-spin" style={{ borderTopColor: ACCENT }} /></div>
    </div>
  </div>
  );
};

// ---------------- Refused: signed in, but not a company owner ----------------
const CEOBlocked: React.FC<{
  message: string;
  onUseAnotherEmail: () => void;
  onChangeRole?: () => void;
}> = ({ message, onUseAnotherEmail, onChangeRole }) => {
  const { t } = useLanguage();
  return (
  <div className="pt-screen pl-screen">
    <div className="pl-frame" aria-hidden="true" />
    <div className="pl-content">
      <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />
      <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>
      <div className="pl-eyebrow">{t('ceoAccessEyebrow')}</div>
      <h1 className="pl-title">{t('ceoNotAvailable')}</h1>
      <p className="pl-sub">{t('ceoNoCompanyLinked')}</p>

      <div className="pl-sheet">
        <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
          <ShieldCheck size={18} color={ACCENT} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, lineHeight: 1.55, color: theme.slate, margin: 0 }}>
            {message || t('ceoNotOwner')}
          </p>
        </div>
        <div className="pl-links" style={{ marginTop: 18 }}>
          <button className="pl-link" onClick={onUseAnotherEmail}>{t('ceoUseDifferentEmail')}</button>
        </div>
      </div>

      {onChangeRole && (
        <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
      )}
    </div>
  </div>
  );
};

// ---------------- Sign-in fallback: email code ----------------
// Only reached when there's no Asset Desk session to exchange (a company owner
// who isn't an investor) or when the exchange was refused. The code is issued
// and checked by the CRM — it's the side that knows which emails are owners.
const CEOLogin: React.FC<{
  notice?: string;
  onSignedIn: (token: string, d: CeoSummary) => void;
  onPreview: () => void;
  onChangeRole?: () => void;
}> = ({ notice, onSignedIn, onPreview, onChangeRole }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const configured = ceoApiConfigured();

  const sendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setErr(t('ceoEnterEmail')); return; }
    setErr(''); setBusy(true);
    try {
      await ceoRequestCode(e);
      setInfo(`${t('ceoCodeSentTo')} ${e}`);
      setStep('code');
    } catch (ex: any) {
      setErr(ex?.message || t('ceoCodeSendFailed'));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { setErr(t('ceoEnterCode')); return; }
    setErr(''); setBusy(true);
    try {
      const token = await ceoVerifyCode(email, code);
      onSignedIn(token, await ceoSummary(token));
    } catch (ex: any) {
      setErr(ex?.message || t('ceoCodeInvalid'));
    } finally { setBusy(false); }
  };

  const onKey = (fn: () => void) => (e: React.KeyboardEvent) => { if (e.key === 'Enter') fn(); };

  return (
    <div className="pt-screen pl-screen">
      <div className="pl-frame" aria-hidden="true" />

      <div className="pl-content">
        <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="pl-logo" />

        <div className="pl-rule" aria-hidden="true"><span /><i>&#9670;</i><span /></div>

        <div className="pl-eyebrow">{t('ceoAccessEyebrow')}</div>
        <h1 className="pl-title">{step === 'email' ? t('ceoAccessTitle') : t('ceoCheckInbox')}</h1>
        <p className="pl-sub">
          {step === 'email'
            ? t('ceoLoginSub')
            : t('ceoCodeSub')}
        </p>

        <div className="pl-sheet">
          {step === 'email' ? (
            <>
              <label className="pl-label" htmlFor="ceo-email">{t('email')}</label>
              <input
                id="ceo-email"
                className="pl-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={onKey(sendCode)}
                autoComplete="username"
                placeholder="you@company.com"
              />
              <button onClick={sendCode} disabled={busy || !configured} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t('ceoSendCode')}
              </button>
            </>
          ) : (
            <>
              <label className="pl-label" htmlFor="ceo-code">{t('ceoCodeLabel')}</label>
              <input
                id="ceo-code"
                className="pl-input pl-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={onKey(verify)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
              />
              <button onClick={verify} disabled={busy} className="pl-btn">
                {busy ? <span className="pt-spin" /> : t('ceoVerifyContinue')}
              </button>
              <div className="pl-links">
                <button className="pl-link" onClick={() => { setStep('email'); setCode(''); setErr(''); setInfo(''); }}>{t('ceoChangeEmail')}</button>
                <button className="pl-link" onClick={sendCode} disabled={busy}>{t('ceoResendCode')}</button>
              </div>
            </>
          )}

          {/* Sample figures are a development aid only. Never offer them in a
              production build — a placeholder company's accounts must not be
              reachable by a real user who taps CEO out of curiosity. */}
          {step === 'email' && !isProdBuild && (
            <div className="pl-links">
              <button className="pl-link" onClick={onPreview} disabled={busy}>{t('ceoViewPreview')}</button>
            </div>
          )}

          {!configured && !err && (
            <p className="pl-note">
              {t('ceoNotConfigured')}
            </p>
          )}
          {configured && notice && !err && !info && <p className="pl-note">{notice}</p>}
          {info && !err && <p className="pl-note">{info}</p>}
          {err && <p className="pl-err">{err}</p>}
        </div>

        {onChangeRole && (
          <button onClick={onChangeRole} className="pl-changerole">← {t('pcChangeRole')}</button>
        )}
      </div>
    </div>
  );
};

// ---------------- Executive summary ----------------
const Executive: React.FC<{ d: CeoSummary; onGoto: (tab: string) => void }> = ({ d, onGoto }) => {
  const { t } = useLanguage();
  const profit = useCountUp(Math.round(d.profitYtd));

  return (
    <div className="pt-stagger" style={{ padding: '14px 16px 24px' }}>
      {/* Entity line */}
      <div style={{ margin: '2px 2px 14px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          {d.year} {t('ceoYearToDate')}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: theme.ink, marginTop: 2, letterSpacing: '-0.3px' }}>
          {d.company.name}
        </div>
        {(d.company.jurisdiction || d.company.trn) && (
          <div style={{ fontSize: 12, color: theme.slate, marginTop: 3 }}>
            {[d.company.jurisdiction, d.company.trn ? `TRN ${d.company.trn}` : null].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {/* Profit hero */}
      <div className="pt-card pt-accent ceo-accent" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 600 }}>{t('profit')} {d.year}</span>
          <span style={{ ...pill('rgba(255,255,255,0.22)', '#fff'), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {d.profitYtd >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {d.marginPct.toFixed(1)}% {t('ceoMargin')}
          </span>
        </div>
        <div style={{ color: '#fff', fontSize: 30, fontWeight: 800, marginTop: 10, letterSpacing: -0.5 }}>
          {d.currency} {profit.toLocaleString('en-AE')}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12.5 }}>
            {t('ceoRevenue')} {money(d.revenueYtd, d.currency)}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12.5 }}>
            {t('expenses')} {money(d.expensesYtd, d.currency)}
          </span>
        </div>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Kpi icon={Landmark} label={t('ceoOutstanding')} value={money(d.receivableTotal, d.currency)} onClick={() => onGoto('receivables')} />
        <Kpi
          icon={AlertTriangle}
          label={t('overdueLabel')}
          value={money(d.overdueTotal, d.currency)}
          sub={`${d.overdueCount} ${d.overdueCount === 1 ? t('ceoInvoice') : t('ceoInvoices')}`}
          tone={d.overdueTotal > 0 ? theme.danger : undefined}
          onClick={() => onGoto('receivables')}
        />
        <Kpi
          icon={Receipt}
          label={d.vat ? `${t('ceoNetVat')} · ${d.vat.periodLabel}` : t('ceoNetVat')}
          value={d.vat ? money(d.vat.netVat, d.currency) : '—'}
          sub={d.vat ? `${t('ceoDuePrefix')} ${fmtDate(d.vat.dueDate)}` : undefined}
          onClick={() => onGoto('tax')}
        />
        <Kpi
          icon={ShieldCheck}
          label={t('ceoCompliance')}
          value={complianceHeadline(d, t)}
          sub={d.compliance.licence?.date ? `${t('ceoLicence')} ${fmtDate(d.compliance.licence.date)}` : undefined}
          tone={complianceTone(d)}
          onClick={() => onGoto('compliance')}
        />
      </div>

      {/* Cash at bank — hidden entirely when there are no accounts on file */}
      <CashAtBank bank={d.bank} />

      {/* Cashflow */}
      <SectionHeader title={`${t('cashflow')} ${d.year}`} note={t('ceoNetOfVat')} />
      <div className="pt-card" style={{ padding: '16px 12px 12px', marginBottom: 16 }}>
        <CashflowChart points={d.monthly} currency={d.currency} />
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 10 }}>
          <Legend color={ACCENT} label={t('ceoRevenue')} />
          <Legend color={theme.slateLight} label={t('expenses')} />
        </div>
      </div>

      {/* Top customers */}
      <SectionHeader title={t('ceoTopCustomers')} note={t('ceoRevenueLower')} />
      <div className="pt-card" style={{ padding: 16, marginBottom: 16 }}>
        <RankList rows={d.topCustomers} currency={d.currency} icon={Users2} empty={t('ceoNoRevenue')} />
      </div>

      {/* Top suppliers */}
      <SectionHeader title={t('ceoTopSuppliers')} note={t('ceoSpendLower')} />
      <div className="pt-card" style={{ padding: 16 }}>
        <RankList rows={d.topSuppliers} currency={d.currency} icon={Truck} empty={t('ceoNoExpenses')} />
      </div>
    </div>
  );
};

// ---------------- Cash at bank ----------------
// `total` covers the base-currency accounts only. Accounts held in another
// currency are listed with their own currency and are deliberately NOT folded
// into the total — converting them here would invent an FX rate we don't have.
const CashAtBank: React.FC<{ bank?: CeoBank }> = ({ bank }) => {
  const { t } = useLanguage();
  if (!bank || !bank.accounts || bank.accounts.length === 0) return null;

  // The freshness line uses lastUpdated (when the record was edited), falling
  // back to asOf (the balance date the client typed) — two different things.
  const freshness = bank.lastUpdated || bank.asOf;
  const hasOtherCurrency = bank.accounts.some(
    (a) => (a.currency || '').toUpperCase() !== (bank.currency || '').toUpperCase(),
  );

  return (
    <>
      <SectionHeader title={t('ceoCashAtBank')} note={hasOtherCurrency ? `${bank.currency} ${t('ceoAccountsInTotal')}` : undefined} />
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wallet size={16} color={ACCENT} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.slate, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('ceoTotal')}
          </span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: theme.ink, marginTop: 6, letterSpacing: -0.4, fontVariantNumeric: 'lining-nums tabular-nums' }}>
          {money(bank.total, bank.currency)}
        </div>

        <div style={{ marginTop: 14, borderTop: `1px solid ${theme.line}` }}>
          {bank.accounts.map((a, i) => (
            <div
              key={`${a.name}-${i}`}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 12, padding: '11px 0',
                borderBottom: i === bank.accounts.length - 1 ? 'none' : `1px solid ${theme.line}`,
              }}
            >
              <span style={{ fontSize: 14.5, color: theme.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.name}
              </span>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink, whiteSpace: 'nowrap', fontVariantNumeric: 'lining-nums tabular-nums' }}>
                {money(a.balance, a.currency)}
              </span>
            </div>
          ))}
        </div>

        {freshness && (
          <p style={{ fontSize: 12.5, color: theme.slate, margin: '12px 0 0' }}>
            {t('ceoBalancesUpdated')} {fmtDate(freshness)}
          </p>
        )}
      </div>
    </>
  );
};

// ---------------- Receivables ----------------
const Receivables: React.FC<{ d: CeoSummary }> = ({ d }) => {
  const { t } = useLanguage();
  const collected = Math.max(0, d.receivableTotal - d.overdueTotal);
  const currentPct = d.receivableTotal ? Math.round((collected / d.receivableTotal) * 100) : 0;

  return (
    <div style={{ padding: '16px 16px 24px' }}>
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.slate }}>{t('ceoTotalOutstanding')}</div>
        <div style={{ fontSize: 27, fontWeight: 800, color: theme.ink, marginTop: 2, letterSpacing: -0.5 }}>
          {money(d.receivableTotal, d.currency)}
        </div>
        <div className="pt-progress-track" style={{ margin: '12px 0 10px' }}>
          <div className="pt-progress-fill" style={{ width: `${currentPct}%`, background: `linear-gradient(90deg, #14213A, ${ACCENT})` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
          <span style={{ color: theme.slate }}>{money(collected, d.currency)} {t('ceoWithinTerms')}</span>
          <span style={{ color: d.overdueTotal > 0 ? theme.danger : theme.slate, fontWeight: 600 }}>
            {money(d.overdueTotal, d.currency)} {t('ceoOverdueLower')}
          </span>
        </div>
      </div>

      {d.overdueTotal > 0 && (
        <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 12, background: theme.dangerBg, color: theme.danger, marginBottom: 16 }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5, fontWeight: 600 }}>
            {d.overdueCount} {d.overdueCount === 1 ? t('ceoInvoice') : t('ceoInvoices')} {t('ceoPastDue')} ·{' '}
            {money(d.overdueTotal, d.currency)}. {t('ceoChaseFirst')}
          </p>
        </div>
      )}

      <SectionHeader title={t('ceoLargestOverdue')} />
      {d.overdue.length === 0 ? (
        <p style={{ textAlign: 'center', color: theme.slate, fontSize: 13, padding: 20 }}>
          {t('ceoNothingOverdue')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {d.overdue.map((o, i) => {
            const late = o.dueDate ? -(daysUntil(o.dueDate) ?? 0) : null;
            return (
              <div key={i} className="pt-card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `3px solid ${theme.danger}` }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: theme.dangerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={19} color={theme.danger} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.customer}
                  </div>
                  <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 1 }}>
                    {[o.invoiceNo, o.dueDate ? `${t('ceoDueLower')} ${fmtDate(o.dueDate)}` : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink }}>{money(o.amount, d.currency)}</div>
                  {late !== null && late > 0 && (
                    <span style={{ ...pill(theme.dangerBg, theme.danger), marginTop: 4 }}>{late}{t('ceoDayShort')} {t('ceoLate')}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- VAT ----------------
const Vat: React.FC<{ d: CeoSummary }> = ({ d }) => {
  const { t } = useLanguage();
  if (!d.vat) {
    return (
      <div style={{ padding: '16px 16px 24px' }}>
        <div className="pt-card" style={{ padding: 20, textAlign: 'center' }}>
          <Receipt size={26} color={theme.slateLight} />
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.ink, marginTop: 10 }}>{t('ceoNoVatPeriod')}</div>
          <p style={{ fontSize: 13, color: theme.slate, marginTop: 4 }}>
            {t('ceoNoVatPeriodSub')}
          </p>
        </div>
      </div>
    );
  }

  const v = d.vat;
  const dueIn = daysUntil(v.dueDate);
  const payable = v.netVat >= 0;

  return (
    <div style={{ padding: '16px 16px 24px' }}>
      <div className="pt-card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: theme.slate }}>{v.periodLabel}</span>
          <span style={pill(dueIn !== null && dueIn <= 14 ? theme.warningBg : '#f3f4f6', dueIn !== null && dueIn <= 14 ? theme.warning : theme.slate)}>
            <CalendarClock size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
            {dueIn === null ? fmtDate(v.dueDate) : dueIn < 0 ? `${-dueIn}${t('ceoDayShort')} ${t('ceoOverdueLower')}` : `${t('ceoDueIn')} ${dueIn}${t('ceoDayShort')}`}
          </span>
        </div>
        <div style={{ fontSize: 27, fontWeight: 800, color: payable ? theme.ink : theme.success, marginTop: 6, letterSpacing: -0.5 }}>
          {money(Math.abs(v.netVat), d.currency)}
        </div>
        <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 2 }}>
          {payable ? t('ceoPayableFta') : t('ceoRefundableFta')} · {t('ceoFilingDue')} {fmtDate(v.dueDate)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <Kpi icon={TrendingUp} label={t('ceoOutputVat')} value={money(v.outputVat, d.currency)} sub={t('ceoChargedOnSales')} />
        <Kpi icon={TrendingDown} label={t('ceoInputVat')} value={money(v.inputVat, d.currency)} sub={t('ceoReclaimable')} />
      </div>

      <SectionHeader title={t('ceoPeriod')} />
      <div className="pt-card" style={{ padding: 16, marginBottom: 16 }}>
        <Row label={t('ceoCovers')} value={`${fmtDate(v.periodStart)} — ${fmtDate(v.periodEnd)}`} />
        <Row label={`${t('ceoSpend')} ${d.year} ${t('ceoInclVat')}`} value={money(d.spendYtdInclVat, d.currency)} />
        <Row label={`${t('ceoRevenue')} ${d.year} ${t('ceoNet')}`} value={money(d.revenueYtd, d.currency)} last />
      </div>

      <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 12, background: theme.infoBg, color: '#1e40af' }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
          {t('ceoVatNote')}
        </p>
      </div>
    </div>
  );
};

// ---------------- Compliance ----------------
const Compliance: React.FC<{ d: CeoSummary }> = ({ d }) => {
  const { t } = useLanguage();
  const items: CeoExpiry[] = useMemo(() => {
    const list: CeoExpiry[] = [];
    if (d.compliance.licence) list.push(d.compliance.licence);
    return [...list, ...d.compliance.visas].sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999));
  }, [d]);

  return (
    <div style={{ padding: '16px 16px 24px' }}>
      <div className="pt-card" style={{ padding: 16, marginBottom: 16 }}>
        <Row label={t('ceoEntity')} value={d.company.name} />
        {d.company.jurisdiction && <Row label={t('ceoJurisdiction')} value={d.company.jurisdiction} />}
        {d.company.licenceNumber && <Row label={t('ceoLicenceNo')} value={d.company.licenceNumber} />}
        <Row label="TRN" value={d.company.trn || '—'} last />
      </div>

      <SectionHeader title={t('ceoRenewals')} note={t('ceoNext90Days')} />
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: theme.slate, fontSize: 13, padding: 20 }}>
          {t('ceoNothingExpiring')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((x, i) => {
            const tone = expiryTone(x.days);
            return (
              <div key={i} className="pt-card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `3px solid ${tone.color}` }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: tone.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={19} color={tone.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.ink }}>{x.label}</div>
                  <div style={{ fontSize: 12.5, color: theme.slate, marginTop: 1 }}>
                    {x.date ? fmtDate(x.date) : t('ceoNoDateOnFile')}
                  </div>
                </div>
                <span style={pill(tone.bg, tone.color)}>{expiryText(x.days, t)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Cashflow chart (inline SVG — no chart dependency) ----------------
const CashflowChart: React.FC<{ points: { month: string; revenue: number; expenses: number }[]; currency: string }> = ({ points, currency }) => {
  const { t } = useLanguage();
  const W = 320, H = 132, PAD_L = 4, PAD_R = 4, PAD_T = 8, PAD_B = 18;
  // Trailing empty months (a year still in progress) would flatten the line to
  // zero, so plot only up to the last month that has any activity.
  const lastActive = points.reduce((last, p, i) => (p.revenue || p.expenses ? i : last), 0);
  const rows = points.slice(0, Math.max(1, lastActive + 1));
  const max = Math.max(...rows.map((r) => Math.max(r.revenue, r.expenses)), 1);
  const x = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / Math.max(1, rows.length - 1);
  const y = (v: number) => PAD_T + (1 - v / max) * (H - PAD_T - PAD_B);
  const line = (key: 'revenue' | 'expenses') => rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(r[key]).toFixed(1)}`).join(' ');
  const area = `${line('revenue')} L${x(rows.length - 1).toFixed(1)},${(H - PAD_B).toFixed(1)} L${x(0).toFixed(1)},${(H - PAD_B).toFixed(1)} Z`;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: theme.slateLight, padding: '0 4px 4px' }}>
        <span>{t('ceoPeak')} {compact(max, currency)}</span>
        <span>{rows.length} {rows.length === 1 ? t('ceoMonth') : t('ceoMonths')}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={t('ceoChartAria')}>
        <defs>
          <linearGradient id="ceoRev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity="0.28" />
            <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={PAD_L} x2={W - PAD_R} y1={y(max * f)} y2={y(max * f)} stroke={theme.line} strokeDasharray="3 3" />
        ))}
        <path d={area} fill="url(#ceoRev)" />
        <path d={line('expenses')} fill="none" stroke={theme.slateLight} strokeWidth="1.5" />
        <path d={line('revenue')} fill="none" stroke={ACCENT} strokeWidth="2.2" strokeLinejoin="round" />
        {rows.map((r, i) => (
          (i === 0 || i === rows.length - 1 || i % 2 === 0) && (
            <text key={r.month} x={x(i)} y={H - 4} fontSize="9" fill={theme.slateLight} textAnchor={i === 0 ? 'start' : i === rows.length - 1 ? 'end' : 'middle'}>
              {r.month}
            </text>
          )
        ))}
      </svg>
    </>
  );
};

// ---------------- Small pieces ----------------
const SectionHeader: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '0 2px 10px' }}>
    <span style={{ fontSize: 15, fontWeight: 800, color: theme.ink }}>{title}</span>
    {note && <span style={{ fontSize: 11.5, color: theme.slateLight }}>{note}</span>}
  </div>
);

const Kpi: React.FC<{
  icon: React.ComponentType<any>; label: string; value: string; sub?: string;
  tone?: string; onClick?: () => void;
}> = ({ icon: Icon, label, value, sub, tone, onClick }) => {
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Icon size={15} color={tone || ACCENT} />
        <span style={{ fontSize: 11.5, fontWeight: 700, color: theme.slate, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: tone || theme.ink, marginTop: 8, letterSpacing: -0.3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: theme.slate, marginTop: 2 }}>{sub}</div>}
    </>
  );
  const style: React.CSSProperties = { padding: 15, border: 'none', textAlign: 'left', display: 'block', width: '100%' };
  return onClick
    ? <button onClick={onClick} className="pt-card pt-tap" style={style}>{body}</button>
    : <div className="pt-card" style={style}>{body}</div>;
};

const RankList: React.FC<{ rows: { name: string; amount: number }[]; currency: string; icon: React.ComponentType<any>; empty: string }> = ({ rows, currency, icon: Icon, empty }) => {
  if (!rows.length) return <p style={{ fontSize: 13, color: theme.slate, margin: 0 }}>{empty}</p>;
  const max = Math.max(...rows.map((r) => r.amount), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((r, i) => (
        <div key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {i === 0 && <Icon size={14} color={ACCENT} style={{ flexShrink: 0 }} />}
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: theme.ink, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.name}
            </span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: theme.ink }}>{money(r.amount, currency)}</span>
          </div>
          <div className="pt-progress-track" style={{ marginTop: 6, height: 5 }}>
            <div className="pt-progress-fill" style={{ width: `${Math.max(3, (r.amount / max) * 100)}%`, background: ACCENT }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; last?: boolean }> = ({ label, value, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: last ? 'none' : `1px solid ${theme.line}` }}>
    <span style={{ fontSize: 13, color: theme.slate, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: theme.ink, textAlign: 'right', minWidth: 0 }}>{value}</span>
  </div>
);

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: theme.slate }}>
    <span style={{ width: 14, height: 3, borderRadius: 2, background: color }} />
    {label}
  </span>
);

// ---------------- Helpers ----------------
function money(v: number, currency: string): string {
  return `${currency} ${Math.round(v).toLocaleString('en-AE')}`;
}
// Compact form for axis labels only, where the full figure would not fit.
function compact(v: number, currency: string): string {
  if (Math.abs(v) >= 1_000_000) return `${currency} ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${currency} ${Math.round(v / 1000)}k`;
  return money(v, currency);
}
function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function daysUntil(d?: string | null): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  if (isNaN(t)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(t).setHours(0, 0, 0, 0) - today.getTime()) / 86400000);
}
function expiryTone(days: number | null): { color: string; bg: string } {
  if (days === null) return { color: theme.slate, bg: '#f3f4f6' };
  if (days < 0) return { color: theme.danger, bg: theme.dangerBg };
  if (days <= 30) return { color: theme.warning, bg: theme.warningBg };
  return { color: theme.success, bg: theme.successBg };
}
// `t` is passed in: these are plain helpers, so they must not call the hook.
function expiryText(days: number | null, t: (k: string) => string): string {
  if (days === null) return t('ceoNoDate');
  if (days < 0) return `${-days}${t('ceoDayShort')} ${t('ceoExpiredSuffix')}`;
  if (days === 0) return t('ceoExpiresToday');
  return `${days}${t('ceoDayShort')} ${t('ceoLeft')}`;
}
function complianceHeadline(d: CeoSummary, t: (k: string) => string): string {
  const soonest = [d.compliance.licence, ...d.compliance.visas]
    .filter((x): x is CeoExpiry => !!x && x.days !== null)
    .sort((a, b) => a.days! - b.days!)[0];
  if (!soonest) return t('ceoAllClear');
  return expiryText(soonest.days, t);
}
function complianceTone(d: CeoSummary): string | undefined {
  const soonest = [d.compliance.licence, ...d.compliance.visas]
    .filter((x): x is CeoExpiry => !!x && x.days !== null)
    .sort((a, b) => a.days! - b.days!)[0];
  if (!soonest) return undefined;
  const t = expiryTone(soonest.days);
  return t.color === theme.success ? undefined : t.color;
}
function pill(bg: string, color: string): React.CSSProperties {
  return { display: 'inline-block', fontSize: 11, fontWeight: 700, color, background: bg, padding: '3px 9px', borderRadius: 99 };
}

export default CEOPortal;
